import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { isTalrooEndDateLocked } from '@/components/pages/edit-campaign/is-talroo-end-date-locked';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';
import { POOL } from '@/types/channels';
import { getChannelName } from '@/utils/channel-names';

import { useBudgetEditStart } from '../use-budget-edit-start';

import { ChannelSwitcher } from './channel-switcher';
import { DurationLockedBanner } from './duration-locked-banner';
import { EditWarningBanner } from './edit-warning-banner';

interface OtherChannelsDialogProps {
  channelSlug: string;
}

export function OtherChannelsDialog({ channelSlug }: OtherChannelsDialogProps) {
  const {
    campaign,
    closeForm,
    hookForm,
    onFormSubmit,
    isSubmitting,
    isFormDirty,
    isCompleted,
    isFetching,
    isScheduled,
  } = useEditCampaignContext();
  const isReadonly = campaign?.is_readonly;
  const isTargetMetricsEditable =
    campaign?.permissions.is_target_metrics_editable;
  const channelDetails = campaign?.sourcing_channel;

  const { reset: resetForm, handleSubmit } = hookForm;
  const isPool = channelSlug === POOL;
  // Identity of the purchase being edited — not the campaign object. A refetch
  // returns a new campaign reference and reseeds the form only while it is
  // untouched, so fresh server values replace pristine defaults and in-progress
  // edits survive. The effect also runs when the form turns pristine again, so a
  // refetch skipped during editing lands once the edits are undone.
  const purchaseId = campaign?.sourcing_purchase_id;
  const seededKeyRef = useRef<string>();

  // Readonly campaigns already use EditWarningBanner for duration. Once budget is
  // editable again, this banner is the reason the dates stay locked.
  const showDurationLockedBanner =
    !isReadonly &&
    isTalrooEndDateLocked({
      channelSlug,
      isScheduled,
      isCompleted,
    });

  useEffect(() => {
    if (!campaign || !purchaseId) return;

    const seededKey = `${purchaseId}:${channelSlug}`;
    if (seededKeyRef.current === seededKey && isFormDirty) return;
    seededKeyRef.current = seededKey;

    resetForm({
      starts_at: campaign.starts_at,
      end_at: campaign.end_at ?? null,
      budget: campaign.budget || null,
      budget_type: campaign.budget_type ?? 'lifetime',
      end_at_type: campaign.end_at_type ?? 'fixed',
      expected_cpa: campaign.expected_cpa || null,
      target_applicants: null,
      expected_conversion: campaign.expected_conversion || null,
      target_hires: null,
      is_location_sensitive: campaign.is_location_sensitive,
    });
  }, [campaign, purchaseId, channelSlug, isFormDirty, resetForm]);
  useBudgetEditStart();

  return (
    <Dialog open={!!channelSlug} onOpenChange={() => closeForm()}>
      <DialogContent className="min-w-[640px]">
        <DialogHeader>
          <DialogTitle>
            {isCompleted || isReadonly ? (
              <FormattedMessage
                defaultMessage="Campaign details"
                description="Read-only modal title when campaign cannot be edited"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Edit Campaign"
                description="Modal title for changing a non-Reach channel campaign"
              />
            )}
          </DialogTitle>
        </DialogHeader>

        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <DialogBody className="flex flex-col gap-6 p-6">
            {isFetching ? (
              <ChannelIcon.Skeleton
                variant="large"
                size="lg"
                className="mx-auto"
              />
            ) : (
              <ChannelIcon
                channelSlug={channelSlug}
                variant="large"
                size="lg"
                className="mx-auto max-w-full"
                channelName={
                  channelDetails?.name ?? getChannelName(channelSlug)
                }
                logoSquareUrl={channelDetails?.logo_square}
                logoRectangleUrl={channelDetails?.logo_rectangle}
              />
            )}
            {isReadonly && !isPool && <EditWarningBanner />}
            {showDurationLockedBanner && <DurationLockedBanner />}
            <ChannelSwitcher channelSlug={channelSlug} />
          </DialogBody>

          <DialogFooter>
            {!isCompleted &&
              (!isReadonly || isTargetMetricsEditable) &&
              !isPool && (
                <Button type="submit" disabled={!isFormDirty || isSubmitting}>
                  {isSubmitting && <Loader2 className="animate-spin" />}
                  <FormattedMessage
                    defaultMessage="Update campaign"
                    description="Submit changes for this channel’s budget and schedule"
                  />
                </Button>
              )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
